import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, Leaf } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/auth";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setPending(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 1600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setPending(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center px-4 py-12">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
        <Leaf className="w-7 h-7 text-green-600" />
      </div>
      <h1 className="text-2xl font-semibold text-slate-900 mt-5 text-center">Choose a new password</h1>

      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mt-8">
        {!token ? (
          <div data-testid="reset-invalid-token" className="p-4 rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-800">
            This reset link is invalid. Request a new one from the sign-in page.
          </div>
        ) : done ? (
          <div data-testid="reset-success-message" className="p-4 rounded-xl border border-emerald-200 bg-emerald-50">
            <div className="text-sm font-semibold text-emerald-900">Password updated</div>
            <p className="text-xs text-emerald-800 mt-1.5">Taking you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">New Password</label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} required minLength={6} value={pw} onChange={e => setPw(e.target.value)}
                  className="h-11 rounded-xl bg-white pr-11" data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  title={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-500 hover:text-slate-700"
                  data-testid="reset-password-toggle">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Confirm Password</label>
              <div className="relative">
                <Input type={showPw2 ? "text" : "password"} required minLength={6} value={pw2} onChange={e => setPw2(e.target.value)}
                  className="h-11 rounded-xl bg-white pr-11" data-testid="reset-confirm-input" />
                <button type="button" onClick={() => setShowPw2(v => !v)}
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  title={showPw2 ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-slate-500 hover:text-slate-700"
                  data-testid="reset-confirm-toggle">
                  {showPw2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={pending} data-testid="reset-submit-button"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors disabled:opacity-60">
              {pending ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
        <div className="text-center mt-4">
          <Link to="/login" className="text-sm text-blue-600 hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
